import { useState } from "react";
import { Link, useParams } from "react-router";
import type { Moment, Performance, PerformanceDetail, PerformanceKind, Performer, Work } from "../api/types";
import { UNAVAILABLE_TEXT } from "../api/errors";
import { useGate, useMe, useServices, useSync } from "../app/services";
import { useAction } from "../app/useAction";
import { usePlaybackActions } from "../app/usePlaybackActions";
import { useResource } from "../app/useResource";
import { EnqueueButton, MomentChip, PerformanceCard, PostCard, SaveButton } from "../components/cards";
import { Icon } from "../components/Icon";
import {
  KindChoice,
  PerformedDateField,
  PerformerPicker,
  RangeFields,
  WorkPicker,
  readRange,
  type RangeValue,
} from "../components/pickers";
import { AddToPlaylistButton } from "../components/playlists";
import { Dialog, EmptyState, PageHeader, Pill, ResourceView, Thumb } from "../components/ui";
import { kindLabel, performerNames } from "../lib/labels";
import { dateInputToIso, formatDate, formatMs, isoToDateInput, performedDateProblem, segmentLength } from "../lib/time";
import { watchUrl } from "../lib/youtube";
import { isDedicatedMix } from "../lib/mix";

/**
 * 歌唱ページ：元動画と区間、聴きどころ（公開のここ好き）、紹介投稿、同じ曲の別の歌唱。登録の修正。
 * ここ好きの時刻を押しても共有プレイヤーは動かさない。移動はDJの専用操作（確認つき）
 */
export function PerformancePage() {
  const { id = "" } = useParams();
  const { api } = useServices();
  const res = useResource("performances.get", () => api.getPerformance(id), [id], ["posts", "library"]);
  return (
    <div className="page">
      <ResourceView resource={res} what="歌唱" rows={5}>
        {(d) => <PerformanceView detail={d} reload={res.reload} />}
      </ResourceView>
    </div>
  );
}

function PerformanceView({ detail, reload }: { detail: PerformanceDetail; reload: () => void }) {
  const p = detail.performance;
  const me = useMe();
  const seg = p.segment;
  const length = segmentLength(seg);
  const canEdit = !!me && (me.id === p.owner_id || me.can_moderate);
  const [editing, setEditing] = useState<null | "meta" | "segment">(null);

  return (
    <>
      <PageHeader
        title={p.work.title}
        lead={`${performerNames(p)} ・ ${kindLabel(p.kind)}${p.performed_at ? ` ・ ${formatDate(p.performed_at)}` : ""}`}
      />

      <section className="perf-hero" aria-label="歌唱の情報">
        <div className="perf-hero__art">
          <Thumb videoId={seg.video_id} title={p.work.title} size="l" />
        </div>
        <div className="perf-hero__body">
          {!p.available && (
            <p className="notice notice--danger">
              <Icon name="warn" className="notice__icon" />
              <span>{UNAVAILABLE_TEXT[p.unavailable_reason ?? "unknown"]}。キューに入れても再生されません。保存や投稿は残ります。</span>
            </p>
          )}
          <dl className="defs">
            <div>
              <dt>元動画</dt>
              <dd>
                <a href={watchUrl(seg.video_id, seg.start_ms)} target="_blank" rel="noopener noreferrer">
                  {p.video_title || "YouTubeで開く"}
                  <Icon name="external" className="inline-icon" />
                  <span className="visually-hidden">（新しいタブ・区間の開始から）</span>
                </a>
              </dd>
            </div>
            <div>
              <dt>区間</dt>
              <dd>
                {formatMs(seg.start_ms)}〜{formatMs(seg.end_ms)}（{formatMs(length)}）
                <span className="muted"> ・ 区間の版 v{seg.version}</span>
              </dd>
            </div>
            <div>
              <dt>曲</dt>
              <dd>
                <Link to={`/works/${p.work.id}`}>{p.work.title}</Link>
                {(p.work.aliases ?? []).length > 0 && <span className="muted">（{(p.work.aliases ?? []).join("・")}）</span>}
              </dd>
            </div>
          </dl>
          <div className="action-row">
            <EnqueueButton performance={p} />
            <SaveButton target={{ performance_id: p.id }} label={`「${p.work.title}」（${performerNames(p)}）`} />
            <AddToPlaylistButton performance={p} />
            <Link className="btn btn-secondary btn-small" to={`/compose?performance=${p.id}`}>
              <Icon name="edit" className="btn-icon" />
              おすすめを書く
            </Link>
          </div>
        </div>
      </section>

      <Highlights performance={p} moments={detail.moments} />

      <section className="section" aria-labelledby="sec-posts">
        <h2 id="sec-posts" className="section-title">
          紹介の投稿 <span className="count">{detail.posts.length}</span>
        </h2>
        {detail.posts.length > 0 ? (
          <ul className="post-list">
            {detail.posts.map((post) => (
              <PostCard key={post.id} post={{ ...post, performance: post.performance ?? p }} showPerformance={false} />
            ))}
          </ul>
        ) : (
          <EmptyState title="まだ紹介がありません" icon="edit">
            <Link to={`/compose?performance=${p.id}`}>最初のおすすめを書く</Link>
          </EmptyState>
        )}
      </section>

      <section className="section" aria-labelledby="sec-siblings">
        <h2 id="sec-siblings" className="section-title">
          「{p.work.title}」の別の歌唱 <span className="count">{detail.siblings.length}</span>
        </h2>
        {detail.siblings.length > 0 ? (
          <ul className="perf-list">
            {detail.siblings.map((s) => (
              <PerformanceCard key={s.id} performance={s} />
            ))}
          </ul>
        ) : (
          <p className="muted">ほかの歌唱はまだ登録されていません。</p>
        )}
        <p className="section-more">
          <Link to={`/works/${p.work.id}`}>この曲のすべての歌唱を見る</Link>
        </p>
      </section>

      <section className="section" aria-labelledby="sec-edit">
        <h2 id="sec-edit" className="section-title">
          登録の修正
        </h2>
        {canEdit ? (
          <>
            <p className="section-basis">
              曲名や歌った日の誤りなどは「登録の内容を直す」。時間がずれているときは「区間を直す」（新しい版になり、これまでのここ好き・キュー・履歴は元の版のまま残ります）。
            </p>
            <div className="action-row">
              <button type="button" className="btn btn-secondary btn-small" onClick={() => setEditing("meta")}>
                登録の内容を直す
              </button>
              <button type="button" className="btn btn-secondary btn-small" onClick={() => setEditing("segment")}>
                区間を直す
              </button>
            </div>
          </>
        ) : (
          <p className="muted">登録した人と管理者が修正できます。</p>
        )}
      </section>

      {canEdit && (
        <>
          <EditMetaDialog open={editing === "meta"} performance={p} onClose={() => setEditing(null)} onDone={reload} />
          <SegmentDialog open={editing === "segment"} performance={p} onClose={() => setEditing(null)} onDone={reload} />
        </>
      )}
    </>
  );
}

/* ---------------------------------------------------------------- 聴きどころ（公開のここ好き） */

function Highlights({ performance: p, moments }: { performance: Performance; moments: Moment[] }) {
  const sync = useSync();
  const dj = useGate("dj");
  const actions = usePlaybackActions();
  const [seekTo, setSeekTo] = useState<Moment | null>(null);
  const playingThis = isDedicatedMix(sync.playback) ? null : sync.playback?.current?.performance.segment.id;
  return (
    <section className="section" aria-labelledby="sec-moments">
      <h2 id="sec-moments" className="section-title">
        聴きどころ <span className="count">{moments.length}</span>
      </h2>
      <p className="section-basis">
        メンバーが公開した「ここ好き」。時刻を押すと、その瞬間の説明と元動画へのリンクが出ます（VCの再生は動きません）。
      </p>
      {moments.length > 0 ? (
        <ul className="moment-list">
          {moments.map((m) => (
            <li key={m.id} className="moment-row">
              <MomentChip moment={m} performance={p} />
              {dj.ok && playingThis === m.segment_revision_id && (
                <button type="button" className="btn btn-ghost btn-small" onClick={() => setSeekTo(m)}>
                  <Icon name="seek" className="btn-icon" />
                  VCの再生をここへ移動（DJ）
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">まだ公開されたここ好きはありません。VCで聴きながら「ここ好き」を押すと、自分だけの下書きとして残せます。</p>
      )}
      <Dialog
        open={seekTo !== null}
        title="VCの再生位置を移動しますか？"
        onClose={() => setSeekTo(null)}
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setSeekTo(null)}>
              やめる
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!dj.ok || isDedicatedMix(sync.playback)}
              onClick={() => {
                if (seekTo) actions.seek(seekTo.position_ms);
                setSeekTo(null);
              }}
            >
              {seekTo ? `${formatMs(seekTo.position_ms)} へ移動` : "移動"}
            </button>
          </>
        }
      >
        <p>VCに参加している全員の再生が、この位置に移ります。</p>
      </Dialog>
    </section>
  );
}

/* ---------------------------------------------------------------- 修正 */

const KINDS: PerformanceKind[] = ["original", "cover", "live", "stream"];
const isKind = (k: string): k is PerformanceKind => (KINDS as string[]).includes(k);

function EditMetaDialog({
  open,
  performance: p,
  onClose,
  onDone,
}: {
  open: boolean;
  performance: Performance;
  onClose: () => void;
  onDone: () => void;
}) {
  const { api } = useServices();
  const [work, setWork] = useState<Work | null>(p.work);
  const [performers, setPerformers] = useState<Performer[]>(p.performers);
  const [kind, setKind] = useState<PerformanceKind | null>(isKind(p.kind) ? p.kind : null);
  const original = isoToDateInput(p.performed_at);
  const [performedOn, setPerformedOn] = useState(original);
  const [tried, setTried] = useState(false);
  const act = useAction("performances.edit", () =>
    api.editPerformance(p.id, {
      work_id: work!.id,
      performer_ids: performers.map((x) => x.id),
      kind: kind!,
      // 変えていなければ送らない（今の値のまま）。消したら null で解除、選び直したらその日
      ...(performedOn === original ? {} : { performed_at: performedOn ? dateInputToIso(performedOn) : null }),
    }),
  );
  const dateProblem = performedDateProblem(performedOn);
  const invalid = !work || performers.length === 0 || !kind || !!dateProblem;
  return (
    <Dialog
      open={open}
      title="登録の内容を直す"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            やめる
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={act.running || !act.connected}
            aria-busy={act.running || undefined}
            onClick={async () => {
              setTried(true);
              if (invalid) return;
              const r = await act.run();
              if (r.ok) {
                onClose();
                onDone();
              }
            }}
          >
            保存する
          </button>
        </>
      }
    >
      <p className="dialog__lead">時間軸（区間）は変わりません。区間を直すときは「区間を直す」から。</p>
      {!act.connected && <p className="inline-note inline-note--warn">登録の修正は、まだAPIに接続されていません。</p>}
      <WorkPicker value={work} onChange={setWork} error={tried && !work ? "曲を選んでください。" : null} />
      <PerformerPicker
        value={performers}
        onChange={setPerformers}
        error={tried && performers.length === 0 ? "歌った人を1人以上選んでください。" : null}
      />
      <KindChoice value={kind} onChange={setKind} />
      <PerformedDateField value={performedOn} onChange={setPerformedOn} error={dateProblem} />
      {act.error && (
        <p className="inline-note inline-note--danger" role="alert">
          {act.error}
        </p>
      )}
    </Dialog>
  );
}

function SegmentDialog({
  open,
  performance: p,
  onClose,
  onDone,
}: {
  open: boolean;
  performance: Performance;
  onClose: () => void;
  onDone: () => void;
}) {
  const { api } = useServices();
  const [range, setRange] = useState<RangeValue>({ start: formatMs(p.segment.start_ms), end: formatMs(p.segment.end_ms) });
  const [tried, setTried] = useState(false);
  const parsed = readRange(range, p.segment.duration_ms);
  const act = useAction("segments.create", () =>
    api.addSegmentRevision(p.id, { url: p.segment.url, start_ms: parsed.start!, end_ms: parsed.end! }),
  );
  const unchanged = parsed.start === p.segment.start_ms && parsed.end === p.segment.end_ms;
  return (
    <Dialog
      open={open}
      title="区間を直す"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            やめる
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={act.running || !act.connected || unchanged}
            aria-busy={act.running || undefined}
            onClick={async () => {
              setTried(true);
              if (parsed.error) return;
              const r = await act.run();
              if (r.ok) {
                onClose();
                onDone();
              }
            }}
          >
            新しい版として保存
          </button>
        </>
      }
    >
      <p className="dialog__lead">
        いまは v{p.segment.version}（{formatMs(p.segment.start_ms)}〜{formatMs(p.segment.end_ms)}）。直すと v{p.segment.version + 1}{" "}
        になります。これまでのここ好き・キュー・履歴は v{p.segment.version} のまま残ります。
      </p>
      {!act.connected && <p className="inline-note inline-note--warn">区間の修正は、まだAPIに接続されていません。</p>}
      <RangeFields value={range} onChange={setRange} durationMs={p.segment.duration_ms} showErrors={tried} />
      {act.error && (
        <p className="inline-note inline-note--danger" role="alert">
          {act.error}
        </p>
      )}
      <p className="muted">
        <Pill tone="info">元動画</Pill> {formatMs(p.segment.duration_ms)}
      </p>
    </Dialog>
  );
}
